import { ChangeEvent, useEffect, useState } from "react";
import Badge from "@/components/Badge";
import Button from "@/components/Button";
import Input from "@/components/Input";
import Pagination from "@/components/Pagination";
import Table from "@/components/Table";
import HeaderColumn from "@/components/Table/table-header/HeaderColumn";
import { ReactComponent as EYE_ICON } from "@/assets/icons/eye.svg";
import HeaderColumnSort from "@/components/Table/table-header/HeaderColumnSort";
import getNotificationInteractor from "@/@core/interactor/notification/get-notification.interactor";
import { Notification } from "@/@core/entity/notification.entity";
import { stringToDate } from "@/utils/date";
import Modal from "@/components/Modal";
import Dropdown from "@/components/Dropdown";
import TextArea from "@/components/TextArea";
import DatePick from "@/components/DatePicker";
import SelectImage from "@/components/SelectImage";
import AVATAR from "@/assets/icons/avatar-picture.svg";
import CheckBox from "@/components/Checkbox";
import { validateImageResolution } from "@/utils/image";
import uploadFileInteractor from "@/@core/interactor/media/upload-file";
import UploadedImageItem from "../UploadedImageItem";
import createNotificationInteractor from "@/@core/interactor/notification/create-notification.interactor";
import toast, { Toaster } from "react-hot-toast";
import ModalDetail from "../ModalDetail";

let debounceSearch: any;
interface Type {
  label: string;
  value: number;
}

interface TableSectionProps {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const TableSection = (props: TableSectionProps) => {
  const { showModal, setShowModal } = props;

  const [type, setType] = useState<Type>();
  const [title, setTitle] = useState<string>("");
  const [image, setImage] = useState<any>([]);
  const [mediaId, setMediaId] = useState<string[]>([]);
  const [link, setLink] = useState<string>("");
  const [publishDate, setPublishDate] = useState<any>();
  const [scheduleDate, setScheduleDate] = useState<any>(new Date());
  const [publishNow, setPublishNow] = useState<boolean>(false);

  const [notifList, setNotifList] = useState<Notification[]>([]);
  const [keyword, setKeyword] = useState<string>("");
  const [activePage, setActivePage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(1);

  const [showModalDetail, setShowModalDetail] = useState<boolean>(false);
  const [dataDetail, setDataDetail] = useState<any>();

  const getNotifList = async (page: number, keyword = "") => {
    try {
      const response = await getNotificationInteractor({
        page,
        keyword,
        limit: 5,
      });

      const {
        data: { data, pagination },
      } = response;

      setNotifList(data);
      if (pagination) {
        setActivePage(pagination.current_page);
        setTotalPage(pagination.last_page);
      }
    } catch (err) {
      setNotifList([]);
      setActivePage(1);
      setTotalPage(1);
    }
  };

  const resetFilter = () => {
    getNotifList(1, "");
    setKeyword("");
  };

  const onChangePagination = (e: any) => {
    setActivePage(e);
    getNotifList(e);
  };

  const onChangeKeyword = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setKeyword(val);

    clearTimeout(debounceSearch);
    debounceSearch = setTimeout(() => {
      getNotifList(1, val);
    }, 2000);
  };

  const onKeywordEnter = (e: any) => {
    const keyCode = e.keyCode;
    const val = e.target.value;

    if (keyCode === 13) {
      clearTimeout(debounceSearch);
      getNotifList(1, val);
    }
  };

  const getProductType = (type: number) => {
    switch (type) {
      case 1:
        return "Announcement";
      case 2:
        return "Live Streaming";
      case 3:
        return "Product Release";
    }
  };

  const onSelectProductPicture = async (e: ChangeEvent<HTMLInputElement>) => {
    const files: any = e.target.files;

    try {
      if (!files[0].type.startsWith("image/")) {
        alert("The uploaded file must be an image");
        return;
      }

      const isResolutionValid = await validateImageResolution(
        files[0],
        1280,
        720
      );

      if (isResolutionValid) {
        if (files && files.length) {
          const responseUploadFile = await uploadFileInteractor(files[0]);

          setMediaId([...mediaId, responseUploadFile?.data?.data?.id]);

          const temp = JSON.parse(JSON.stringify(image));

          for (let i = 0; i < files.length; i += 1) {
            const objectUrl = URL.createObjectURL(files[i]);
            temp.push(objectUrl);
          }

          setImage(temp);
        }
      } else {
        alert(
          "Image resolution is too high. Maximum resolution allowed is 1280x720"
        );
      }
    } catch (err) {
      console.error(err);
    }
  };

  const onRemoveProductPicture = (index: number) => {
    const temp = JSON.parse(JSON.stringify(image));
    temp.splice(index, 1);
    mediaId.splice(index, 1);
    setImage(temp);
    setMediaId(mediaId.splice(index, 1));
  };

  const getBody = () => {
    const date = new Date();
    const isScheduled = !publishNow;
    const scheduled_at = publishNow
      ? date.toISOString()
      : scheduleDate?.toISOString();

    return {
      type: type?.value,
      is_scheduled: isScheduled,
      title,
      scheduled_at,
      description: "",
      link,
      media_id: mediaId[0],
      data_id: null,
    };
  };

  const resetForm = () => {
    setType(undefined)
    setPublishNow(false)
    setTitle('')
    setPublishDate(null)
    setLink('')
    setMediaId([])
    setImage([])
  }

  const createNotif = async () => {
    try {
      const body = getBody();
      const response = await createNotificationInteractor(body);

      const { status } = response;

      if (status === 201) {
        toast.success("Created");
        getNotifList(activePage);
        setShowModal(!showModal);
      }
    } catch (error: any) {
      const { response } = error;
      const {
        data: { message },
      } = response;

      toast.error(message);
    } finally {
      resetForm();
    }

  };

  const checkDisabled = () => {
    return !type || !title || !mediaId.length || (!publishNow && !publishDate);
  };

  const handleDetail = (data:any) => {
    setDataDetail(data);
    setShowModalDetail(true)
  }

  useEffect(() => {
    getNotifList(activePage);
  }, []);

  return (
    <>
      <div className='table-control-section'>
        <div>
          <label>Search for notification</label>
          <Input
            style={{ width: "360px" }}
            value={keyword}
            withIcon
            type='search'
            placeholder='Search'
            onChange={onChangeKeyword}
            onKeyDown={onKeywordEnter}
          />
        </div>

        <div className='btn-wrapper'>
          <Button
            onClick={resetFilter}
            label='Reset Filter'
            type='secondary'
            size='md'
            disabled={!keyword}
          />
        </div>
      </div>

      <div className='table-section'>
        <Table>
          <thead>
            <tr>
              <HeaderColumn label='Title' alignLabel='text-left' />
              <HeaderColumn label='Type' alignLabel='text-left' />
              <HeaderColumn label='Status' alignLabel='text-left' />
              <HeaderColumn label='Created by' alignLabel='text-left' />
              <HeaderColumnSort
                label='Created at'
                sortType={"asc"}
                onChange={(e) => {}}
                alignLabel='text-center'
              />
              <HeaderColumnSort
                label='Schedule Publish'
                sortType={"asc"}
                onChange={(e) => {}}
                alignLabel='text-center'
              />
              <HeaderColumn label='' alignLabel='text-left' />
            </tr>
          </thead>
          <tbody>
            {notifList.map((notif) => (
              <tr key={notif.id}>
                <td width='25%'>
                  <div className='text-title'>
                    <span>{notif?.title}</span>
                  </div>
                </td>
                <td width='15%'>
                  <div className='text'>
                    <span>{getProductType(notif?.type)}</span>
                  </div>
                </td>
                <td width='100px'>
                  <div className='item-center'>
                    <Badge
                      dot
                      dotColor={notif?.status === 1 ? "#F79009" : "#17B26A"}
                      label={notif?.status === 1 ? "Scheduled" : "Published"}
                      labelColor={notif?.status === 1 ? "#B54708" : "#067647"}
                      backgroundColor={
                        notif?.status === 1 ? "#FFFAEB" : "#ECFDF3"
                      }
                    />
                  </div>
                </td>
                <td width='15%'>
                  <div className='text item-center'>
                    <span>{notif?.created_by ?? '-'}</span>
                  </div>
                </td>
                <td width='20%'>
                  <div className='text item-center'>
                    <span>
                      {stringToDate(
                        notif?.created_at,
                        "dd month yyyy hours:minutes"
                      )}
                    </span>
                  </div>
                </td>
                <td width='20%'>
                  <div className='text item-center'>
                    <span>
                      {stringToDate(
                        notif?.scheduled_at,
                        "dd month yyyy hours:minutes"
                      )}
                    </span>
                  </div>
                </td>
                <td width='20%'>
                  <div className='cta-wrapper'>
                    <button
                      onClick={() => handleDetail(notif)}>
                      <EYE_ICON />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Pagination
          activePage={activePage}
          totalPage={totalPage}
          onChange={onChangePagination}
        />
      </div>
      {showModal && (
        <Modal
          setIsOpen={() => setShowModal(!showModal)}
          type='notification'
          title='Add Notification'
          subtitle='Add Notification description'
          withDevider
          content={
            <div className='modal-content'>
              <div className='row'>
                <div className='label'>
                  <span>
                    Type<span>*</span>
                  </span>
                </div>
                <div className='input'>
                  <Dropdown
                    placeHolder='Select Type'
                    value={type}
                    options={[
                      { label: "Announcement", value: 1 },
                      { label: "Live Streaming", value: 2 },
                      { label: "Product Release", value: 3 },
                    ]}
                    onChange={(e: any) => setType(e)}
                    disabled={false}
                    optionType='text-option'
                    width='400px'
                  />
                </div>
              </div>
              <div className='divider' />
              <div className='row'>
                <div className='label'>
                  <span>
                    Title<span>*</span>
                  </span>
                </div>
                <div className='input'>
                  <TextArea
                    value={title}
                    style={{ width: "400px", height: "68px" }}
                    maxLength={100}
                    withCounter={false}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder='e.g. Apakah kamu pemenangnya? Segera di mulai, pengumuman GIVEAWAY MOTOR dari DRX!  🏍'
                  />
                </div>
              </div>
              <div className='divider' />
              <div className='row'>
                <div className='label'>
                  <span>
                    Image<span>*</span>
                  </span>
                </div>
                <div className='input'>
                  <div className='row space'>
                    <div style={{ marginRight: "16px" }}>
                      {image.map((img: any, index: number) => (
                        <UploadedImageItem
                          key={index}
                          imageSrc={img}
                          onRemove={() => onRemoveProductPicture(index)}
                        />
                      ))}
                      {!image.length && <UploadedImageItem imageSrc={AVATAR} />}
                    </div>
                    <SelectImage
                      onChange={onSelectProductPicture}
                      disabled={image.length === 1}
                      displayIcon={false}
                      desc='PNG or JPG (1280x720px)'
                      width='320px'
                    />
                  </div>
                </div>
              </div>
              <div className='divider' />
              <div className='row'>
                <div className='label'>
                  <span>Link</span>
                </div>
                <div className='input'>
                  <Input
                    style={{ width: "400px" }}
                    type='text'
                    withIcon={false}
                    placeholder='e.g. https://drxasia.com/shop/new-release'
                    value={link}
                    onChange={(e) => setLink(e.target.value)}
                  />
                </div>
              </div>
              <div className='divider' />
              <div className='row'>
                <div className='label'>
                  <span>
                    Publish Date<span>*</span>
                  </span>
                </div>
                <div className='input'>
                  <div className='column'>
                    <div className='row margin-bt'>
                      <div className='checkbox'>
                        <CheckBox
                          checked={publishNow}
                          onChange={() => {
                            setPublishNow(!publishNow);
                            setPublishDate("");
                          }}
                        />
                      </div>
                      <span className='text'>Publish now</span>
                    </div>
                    <DatePick
                      style={{ width: "400px" }}
                      placeholder='Select date'
                      value={publishDate}
                      onChange={(e) => {
                        setScheduleDate(e);
                        setPublishDate(e);
                      }}
                      disabled={publishNow}
                      withTime
                    />
                  </div>
                </div>
              </div>
            </div>
          }
          footer={
            <div className='modal-footer'>
              <Button
                style={{ width: "270px" }}
                type='secondary'
                label='Cancel'
                onClick={() => setShowModal(!showModal)}
              />
              <Button
                style={{ width: "270px" }}
                type='primary'
                label='Save'
                disabled={checkDisabled()}
                onClick={() => createNotif()}
              />
            </div>
          }
        />
      )}
      {showModalDetail && <ModalDetail showModalDetail={showModalDetail} setShowModalDetail={setShowModalDetail} data={dataDetail}/> }
      <Toaster />
    </>
  );
};

export default TableSection;
